import { ComponentProps, FC, useCallback, useMemo } from 'react';

import classNames from 'classnames';

import { SafeMobileCheckProvider } from '@dtci/triton/hooks';

import {
  getMaxWidthBreakpointForMediaQuery,
  getResponsiveBreakpointClassName,
  NavBarContext,
} from '../../NavBar';
import { TestMetadata } from '../../types';

import './SubNavBar.scss';

export interface SubNavBarProps extends ComponentProps<'nav'> {
  /** Custom class name passed down to nav */
  className?: string;
  /** TestMetadata used for nav */
  testMetadata?: TestMetadata;
  /** Pass in your Brand, Controls, and Utility children */
  children?: JSX.Element | JSX.Element[];
  /** Indicate at which breakpoint size the nav bar responsive behavior starts */
  responsiveBreakpoint?: 'sm' | 'md' | 'lg';
}

const SubNavBar: FC<SubNavBarProps> = ({
  className,
  testMetadata,
  children,
  responsiveBreakpoint = 'sm',
  ...otherProps
}) => {
  const getBreakpointClassName = useCallback(
    (baseClassName: string, includeBaseClass = true) =>
      getResponsiveBreakpointClassName(
        baseClassName,
        responsiveBreakpoint,
        includeBaseClass,
      ),
    [responsiveBreakpoint],
  );

  const navBarContextValue = useMemo(
    () => ({
      responsiveBreakpoint,
      getBreakpointClassName,
    }),
    [getBreakpointClassName, responsiveBreakpoint],
  );

  const styles = classNames('sb-subNavBar-nav', className);

  return (
    <NavBarContext.Provider value={navBarContextValue}>
      <SafeMobileCheckProvider
        maxWidth={getMaxWidthBreakpointForMediaQuery(responsiveBreakpoint)}
      >
        <nav className={styles} {...testMetadata} {...otherProps}>
          {children}
        </nav>
      </SafeMobileCheckProvider>
    </NavBarContext.Provider>
  );
};

export default SubNavBar;
